/*
 * Displays History of either an entire FormInstance or of specific FormInstanceElement
 */
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute } from '@angular/router';
import { ListViewEnum } from '../../../shared/components/list-view/list-view.helper';
import { ItemTypeEnum } from '../../enums/item-type.enum';
import { IListItem } from '../../interfaces/ilist-item.interface';
import { KendoGridHelper } from '../../kendo-grid-helper';
import { FormInstance } from '../../models/site-content/form-instance.model';
import { FormInstanceHistoryService } from '../../services/form-instance-history.service';
import { FormInstanceService } from '../../services/form-instance.service';
import { UtilityHelper } from '../../utility.helper';
import { ButtonConfig } from '../list-view/list-view-button-config.model';
import { ScriptableBaseComponent } from '../scriptable-base/scriptable-base.component';
//import { ComponentHierarchyService } from '../../services/component-hierarchy.service';

@Component({
    selector: 'app-form-instance-history',
    templateUrl: './form-instance-history.component.html',
    encapsulation: ViewEncapsulation.None, //enable CSS overrides
    styleUrls: ['./form-instance-history.component.scss'],
    standalone: false
})
export class FormInstanceHistoryComponent extends ScriptableBaseComponent implements OnInit {

    @Input() historyItem: IListItem;
    @Input() formInstance: FormInstance; // added 1/31/2023 for VNEXT-562
    @Input() showHistoryForRichTextField: boolean; // for VNEXT-1215 (restore old history behavior for rich text fields)

    @Output() close: EventEmitter<any> = new EventEmitter();
    @Output() restore: EventEmitter<any> = new EventEmitter();
    @Output() view: EventEmitter<any> = new EventEmitter();

    private historyItems: IListItem[];

    private listItems: IListItem[];
    private logMessages: IListItem[];
    private buttonConfig: any;
    private comparing: boolean = false;
    private selectedVersions: FormInstance[];
    private versionOneForCompare: FormInstance;
    private versionTwoForCompare: FormInstance;
    private diffHtml: string;
    private diffReady: boolean;
    private oneVersionSelected: boolean;
    private twoVersionsSelected: boolean;
    private editComment: boolean = false;
    private selectedItem: any;
    private helper = new KendoGridHelper();

    // Context menu properties.
    private itemContextMenuPosition = { x: '0px', y: '0px' };
    @ViewChild('itemContextMenuTrigger', { read: MatMenuTrigger }) itemContextMenu: MatMenuTrigger;
    columnsConfig: string[];
    
    public constructor(private formInstanceService: FormInstanceService,
        private formInstanceHistoryService: FormInstanceHistoryService,
        private route: ActivatedRoute,
        elForBaseClass: ElementRef)
    {
        //super(componentHierarchyService, elForBaseClass);
        super(elForBaseClass);
    }

    ngOnInit(): void {
        // TODO: consolidate this
        if (this.historyItem.getType() == FormInstance.TypeName) {
            this.formInstanceService.getHistory(this.historyItem.getOriginalVersionId()).then(
                x => {
                    this.listItems = x.Versions;
                    this.logMessages = x.LogMessages;
                    this.setButtonConfig();
                    this.setColumnsConfig();
                }
            );
        } else if (this.historyItem.getType() == ItemTypeEnum.FORM_INSTANCE_ELEMENT_FOR_HISTORY) {
            this.formInstanceService.getFormInstanceElementHistory(this.historyItem.getOriginalVersionId()).then(
                x => {
                    this.listItems = x;
                    this.setButtonConfig(true);
                    this.setColumnsConfig(true);
                }
            );
        } 
    }

    private setColumnsConfig(includeComments: boolean = false) {
        if (includeComments) {
            this.columnsConfig = ['select', 'name', 'property(Comment)', 'modifiedBy', 'modifiedDate'];
        } else {
            this.columnsConfig =['select', 'name', 'modifiedBy', 'modifiedDate'];
        }
    }

    public get Helper(): KendoGridHelper {
        return this.helper;
    }

    public get ColumnsConfig() {
        return this.columnsConfig;
    }

    private filters = { logic: 'and', filters: [] };
    public get Filters() {
        return this.filters;
    }
    public set Filters(filters: any) {
        this.filters = filters;
    }
    public handleClearFilters(): void {
        this.filters = { logic: 'and', filters: [] };
    }

    public get IsBeingFiltered(): boolean {
        return this.filters.filters.length > 0;
    }


    public downloadGridData(dataItem: any) {
        let { formInstanceElementId, formInstanceElementVersionId } = this.getIds(dataItem);
        let url = `${this.formInstanceHistoryService.getBaseGridDownloadUrl()}/${formInstanceElementId}/${formInstanceElementVersionId}`;

        window.open(url, '_blank');
    }

    private getIds(dataItem: any) {
        // Crude parsing logic for now based on what's currently coming back from Dan's function which looks like
        // "Grid -- Some kind of API call to forminstancelementversionid: 49165"
        // "Grid -- Some kind of API call to forminstancelementid: 60583"
        let formInstanceElementParts = dataItem.value?.split('forminstancelementid: ');
        let formInstanceElementVersionParts = dataItem.value?.split('forminstancelementversionid: ');
        let formInstanceElementId = formInstanceElementParts.length == 2 ? formInstanceElementParts[1] : 0;
        let formInstanceElementVersionId = formInstanceElementVersionParts.length == 2 ? formInstanceElementVersionParts[1] : 0;
        return { formInstanceElementId, formInstanceElementVersionId };
    }

    public getGridOutput(dataItem) {
        let { formInstanceElementId, formInstanceElementVersionId } = this.getIds(dataItem);
        return `${formInstanceElementId}/${formInstanceElementVersionId}`;
    }

    // Replace with proper iFieldDef thing
    public isGridElement(dataItem: any) {
        return dataItem.value?.indexOf('Grid --') > -1;
    }

    private setButtonConfig(includeInfoAction: boolean = false) {
        var btnCompare = new ButtonConfig('Compare Selected', 'compare');
        btnCompare.cssClass = 'button-right-margin';
        var btnClose = new ButtonConfig('Close History', 'close');
        btnClose.cssClass = 'button-right-margin';

        this.buttonConfig = {
            right: [
                btnCompare,
                btnClose
            ],
            item_actions: []
        };

        if (includeInfoAction) {
            this.buttonConfig.item_actions.push(new ButtonConfig('Add Comment to', 'comment', 'info'));
        }
    }

    private setListItems(): void {
        this.listItems = this.historyItems;
    }

    public displayEntity(event: any) { }

    public closeHistory() {
        this.close.emit(this.formInstance.id);
    }

    public doOnButtonClick(event: any) {
        if (event.button == 'close') {
            this.close.emit(this.historyItem.getOriginalVersionId());
        } else if (event.button == 'compare') {
            this.doOnCompare(event);
        }
    }

    public doOnViewVersion(event: any) {
        this.view.emit(this.itemContextMenu.menuData.item);
    }

    public doOnCompare(event: any) {
        if (this.selectedVersions.length == 2) {
            this.comparing = true;
            this.diffReady = false;

            let olderVersion = this.selectedVersions[1];
            let newerVersion = this.selectedVersions[0];
            let olderIsVersion = !olderVersion.isOriginalVersion;
            let newerIsVersion = !newerVersion.isOriginalVersion;
            let formInstanceId = this.formInstance?.id || this.historyItem.getId();
            this.formInstanceService.compareVersions(this.historyItem.getType(), formInstanceId, olderVersion.id, olderIsVersion, newerVersion.id, newerIsVersion).then(diff => {
                this.diffHtml = diff.html;
                this.diffReady = true;
            });
        } else {
            alert("Select two versions to compare!");
        }
    }

    public FormInstanceElementsForCompare(): [] {
        return [];
    }

    public doOnRestore(event: any) {
        if (this.historyItem && (this.itemContextMenu.menuData.item.formInstanceVersion == 0 || this.itemContextMenu.menuData.item.version == 0)) {
            alert("Error: Already at this version");
            return;
        }
        if (this.historyItem.getType() == ItemTypeEnum.FORM_INSTANCE_ELEMENT_FOR_HISTORY) {
            this.restore.emit(this.itemContextMenu.menuData.item);
        } else {
            this.formInstanceService.restoreVersion(this.historyItem.getType(), this.itemContextMenu.menuData.item.id).then(res => {
                this.restore.emit(this.itemContextMenu.menuData.item);
            });
        }
    }

    public get LogMessages() {
        return this.logMessages;
    }

    public get ShowTransitionHistory() {
        return this.historyItem.getType() == FormInstance.TypeName;
    }

    private getDeletions(diffs: []): string {
        let html = [];
        for (let diff of diffs) {
            if (diff[0] == -1) {
                html.push(`<del style="background-color:#ffe6e6;">${diff[1]}</del>`)
            } else if (diff[0] == 0) {
                html.push(`<span>${diff[1]}</span>`)
            }
        }
        return html.join("");
    }

    private getInsertions(diffs: []): string {
        let html = [];
        for (let diff of diffs) {
            if (diff[0] == 1) {
                html.push(`<ins style="background-color:#e6ffe6;">${diff[1]}</ins>`)
            } else if (diff[0] == 0) {
                html.push(`<span>${diff[1]}</span>`)
            }
        }
        return html.join();
    }

    public handleExceededMaxSelection() {
        alert('You can only select 2 versions to compare.');
    }

    public doOnItemAction(event: any) {
        console.log(event);
        this.editComment = !this.editComment;
        if (this.editComment) {
            this.selectedItem = event.item;
        } else {
            this.selectedItem = null;
        }
    }

    public cancel() {
        this.editComment = false;
        this.selectedItem = null;
    }

    public saveComment() {
        console.log('saving...');
        this.formInstanceService.updateFormInstanceElementVersionComment(this.selectedItem).then(res => {
            this.selectedItem = null;
        });
    }

    public get EditComment(): boolean {
        return this.editComment;
    }

    public get SelectedItem() {
        return this.selectedItem;
    }

    //TODO: DRY THIS UP -- this same code appears in any ListView parent which needs context menu handling
    public doOnItemContextMenu(event: any) {
        // Set the context menu position.
        this.itemContextMenuPosition.x = event.originalEvent.clientX + 'px';
        this.itemContextMenuPosition.y = event.originalEvent.clientY + 'px';

        // Set menu data.
        this.itemContextMenu.menuData = { 'item': event.item };
        this.itemContextMenu.openMenu();
    }
    public handleLoadMore($event) { }

    public setSelectedVersions(selectedVersions: FormInstance[]) {
        this.selectedVersions = selectedVersions;
        this.versionOneForCompare = selectedVersions[0];
        this.versionTwoForCompare = selectedVersions[1];
        UtilityHelper.runWhenStackClear(() => {
            this.twoVersionsSelected = this.selectedVersions.length == 2; // avoid "changed after checked" error
        });
    }

    public onCloseButtonClick(event: any) {
        this.close.emit(this.historyItem.getOriginalVersionId());
    }

    public get Title(): string {
        return this.historyItem.name ? `${this.historyItem.name} History` : 'History' ;
    }

    public get ItemContextMenuPositionX(): string {
        return (this.itemContextMenuPosition.x);
    }

    public get ItemContextMenuPositionY(): string {
        return (this.itemContextMenuPosition.y);
    }

    public get ListType() {
        return ListViewEnum.FORM_INSTANCE_HISTORY;
    }

    public get ListItems() {
        return this.listItems;
    }

    public get ButtonConfig() {
        return this.buttonConfig;
    }

    public get Comparing() {
        return this.comparing;
    }

    public set Comparing(val: boolean) {
        this.comparing = val;
        if (!this.comparing) {
            this.selectedVersions = null;
            this.versionOneForCompare = null;
            this.versionTwoForCompare = null;
        }
    }

    public get SelectedVersions() {
        return this.selectedVersions;
    }

    public get TwoVersionsSelected() {
        return this.twoVersionsSelected;
    }

    public get VersionOneForCompare() {
        return this.versionOneForCompare;
    }

    public get VersionTwoForCompare() {
        return this.versionTwoForCompare;
    }

    public get DiffHtml(): string {
        return this.diffHtml;
    }


    public get DiffReady(): boolean {
        return this.diffReady;
    }


}
